// «به تقویمم اضافه کن» — منطقِ خالص، بدون react-native و بدون DOM.
//
// ═══ چرا این‌جا فقط لینکِ گوگل هست و نه فایلِ ICS ═══
//
// دو سطحِ متفاوت، دو مکانیزمِ متفاوت — و عمداً هیچ‌کدوم دیگری را تکرار
// نمی‌کند:
//
//   ایمیلِ دعوت   یک فایلِ `.ics` **پیوست** دارد، که جیمیل و اوت‌لوک و میلِ
//                 اپل خودشان به‌صورت یک کارتِ «افزودن به تقویم» بالای ایمیل
//                 نشانش می‌دهند. ساختنش کارِ فرستنده است:
//                 supabase/functions/send-invite-email/index.ts
//   صفحه‌ی دعوت   لینکِ گوگل‌کلندر، که فقط یک URL است. مرورگر فایلِ ICS را
//                 دانلود می‌کند و روی موبایل اغلب هیچ اتفاقی نمی‌افتد؛ لینک
//                 مستقیم تقویم را باز می‌کند.
//
// اگر روزی صفحه هم به فایلِ ICS نیاز پیدا کرد، آن‌وقت سازنده‌اش باید یک‌جا
// بیاید و هر دو از همان‌جا بخوانند — نه اینکه دومی کپی شود.
//
// آن روز رسید (WHOSUP-134): برنامه‌ی قطعی‌شده دکمه‌ی «افزودن به تقویم» با
// فایلِ واقعی می‌خواهد. `buildIcs` پایین حالا نسخه‌ی مرجع است. نسخه‌ی
// send-invite-email هنوز کپیِ خودش را دارد چون انتشارِ تابعِ سرور از این
// مخزن جداست — اولین باری که آن تابع دوباره deploy می‌شود باید از همین‌جا
// import کند و کپی‌اش حذف شود.

/** رخدادی که به تقویم می‌رود. همان زیرمجموعه‌ای از سیگنال که واقعاً لازم است. */
export interface CalendarEvent {
  title: string;
  /** ISO. خالی یعنی سیگنال زمانِ مشخصی ندارد و اصلاً به تقویم نمی‌رود. */
  startsAt?: string | null;
  endsAt?: string | null;
  /** ساعت انتخاب نشده — فقط روز(ها). */
  dateOnly?: boolean | null;
  placeName?: string | null;
  note?: string | null;
  /** لینکِ برگشت به صفحه‌ی دعوت، که ته توضیحاتِ رخداد می‌نشیند. */
  url?: string | null;
}

/** پیش‌فرضِ طولِ رخدادِ بی‌پایان. دو ساعت، همان چیزی که ICS هم می‌گذارد. */
const DEFAULT_MINUTES = 120;

/**
 * `YYYYMMDDTHHMMSSZ` و برای رخدادِ روزمحور `YYYYMMDD` — قالبی که هم
 * گوگل‌کلندر می‌فهمد و هم استانداردِ iCalendar است.
 */
const stamp = (iso: string, dateOnly: boolean): string => {
  const d = new Date(iso).toISOString().replace(/[-:]/g, '');
  return dateOnly ? d.slice(0, 8) : `${d.slice(0, 15)}Z`;
};

/**
 * لینکِ «افزودن به گوگل‌کلندر».
 *
 * سیگنالِ بی‌تاریخ `null` می‌دهد و صفحه دکمه را اصلاً نشان نمی‌دهد — دکمه‌ای
 * که تقویمِ خالی باز کند بدتر از نبودنش است.
 *
 * نکته‌ی روزمحور: گوگل بازه را **باز** می‌خواند، یعنی `DTEND` روزِ بعدِ آخرین
 * روز است. بدون این، رخدادِ یک‌روزه هیچ روزی را نمی‌گیرد و توی تقویم دیده
 * نمی‌شود.
 */
export function googleCalendarUrl(e: CalendarEvent): string | null {
  if (!e.startsAt) return null;
  const dateOnly = e.dateOnly === true;

  const start = new Date(e.startsAt);
  const end = e.endsAt
    ? new Date(e.endsAt)
    : new Date(start.getTime() + DEFAULT_MINUTES * 60_000);
  if (dateOnly) end.setUTCDate(end.getUTCDate() + 1);

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${stamp(start.toISOString(), dateOnly)}/${stamp(end.toISOString(), dateOnly)}`,
  });
  const details = [e.note, e.url].filter(Boolean).join('\n\n');
  if (details) params.set('details', details);
  if (e.placeName) params.set('location', e.placeName);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** خطِ ICS نباید از ۷۵ اوکتت بگذرد؛ ادامه‌اش با یک فاصله در خطِ بعد می‌آید. */
const fold = (line: string): string => line.match(/.{1,73}/g)?.join('\r\n ') ?? line;

const icsEscape = (s: string): string =>
  s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

export interface IcsEvent extends CalendarEvent {
  /**
   * شناسه‌ی پایدارِ رویداد. **باید بینِ ویرایش‌ها ثابت بماند**: تقویمِ گیرنده
   * با همین یکی می‌فهمد «همان رویدادِ قبلی عوض شد» — UID تازه یعنی رویدادِ
   * دوم کنارِ اولی. برای برنامه‌ی قطعی‌شده `plan-<signalId>` بگذار.
   */
  uid: string;
  /**
   * شماره‌ی نسخه‌ی iCalendar — هر بازنشرِ همان UID باید عددِ بزرگ‌تری داشته
   * باشد وگرنه تقویم آپدیت را نمی‌پذیرد. صفر برای اولین نسخه درست است.
   */
  sequence?: number;
  /** `true` یعنی METHOD:CANCEL — تقویمِ گیرنده رویداد را برمی‌دارد. */
  cancelled?: boolean;
  /** لحظه‌ی تولید (DTSTAMP). پارامتر است تا تابع خالص و تست‌پذیر بماند. */
  now?: Date;
}

/**
 * فایلِ تقویمِ iCalendar (RFC 5545).
 *
 * سیگنالِ بی‌تاریخ `null` می‌دهد — رخدادی بدون زمان چیزی نیست که بشود توی
 * تقویم گذاشت؛ همان قراردادِ `googleCalendarUrl`.
 */
export function buildIcs(e: IcsEvent): string | null {
  if (!e.startsAt) return null;
  const dateOnly = e.dateOnly === true;
  const dt = (iso: string) => stamp(iso, dateOnly);
  const prop = dateOnly ? ';VALUE=DATE' : '';

  const start = new Date(e.startsAt);
  const end = e.endsAt
    ? new Date(e.endsAt)
    : new Date(start.getTime() + DEFAULT_MINUTES * 60_000);
  // بازه‌ی روزمحور باز است — بدونِ روزِ اضافه، رخدادِ یک‌روزه هیچ روزی را
  // نمی‌گیرد (همان نکته‌ی googleCalendarUrl).
  if (dateOnly) end.setUTCDate(end.getUTCDate() + 1);

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Pals & Plans//plan//EN',
    'CALSCALE:GREGORIAN',
    `METHOD:${e.cancelled ? 'CANCEL' : 'PUBLISH'}`,
    'BEGIN:VEVENT',
    /**
     * ⚠️ این دامنه دیگر نباید عوض شود.
     *
     * UID شناسه‌ی ماندگارِ یک رخداد در تقویمِ گیرنده است: وقتی بعداً ویرایش یا
     * لغو می‌فرستیم، تقویم از روی **همین رشته** می‌فهمد کدام رخداد را به‌روز
     * کند. اگر دامنه عوض شود، UIDِ همان رخداد فرق می‌کند و تقویمِ طرفِ مقابل
     * به‌جای به‌روزرسانی یک رخدادِ دوم می‌سازد — یعنی دعوتِ تکراری و لغوی که
     * هیچ‌وقت نمی‌نشیند.
     *
     * الان بی‌خطر عوض شد چون هنوز کاربری نداریم و هیچ دعوتی بیرون نیست. از
     * اولین کاربر به بعد، این رشته ثابت است حتی اگر دامنه دوباره عوض شود.
     */
    `UID:${e.uid}@outami.com`,
    `SEQUENCE:${e.sequence ?? 0}`,
    e.cancelled ? 'STATUS:CANCELLED' : null,
    `DTSTAMP:${stamp((e.now ?? new Date(0)).toISOString(), false)}`,
    `DTSTART${prop}:${dt(start.toISOString())}`,
    `DTEND${prop}:${dt(end.toISOString())}`,
    fold(`SUMMARY:${icsEscape(e.title)}`),
    e.note || e.url
      ? fold(`DESCRIPTION:${icsEscape([e.note, e.url].filter(Boolean).join('\n'))}`)
      : null,
    e.url ? fold(`URL:${e.url}`) : null,
    e.placeName ? fold(`LOCATION:${icsEscape(e.placeName)}`) : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  return `${lines.join('\r\n')}\r\n`;
}
